import { useEffect, useState } from 'react';

/**
 * Hook para gerenciar feature flags em produção
 * Permite habilitar/desabilitar funcionalidades via localStorage
 * 
 * @param {string} featureName - Nome da feature
 * @param {boolean} defaultValue - Valor padrão (false em produção)
 * @returns {object} { isEnabled, enable, disable }
 */
export const useFeatureFlag = (featureName, defaultValue = false) => {
  const storageKey = `feature_flag_${featureName}`;
  
  const [isEnabled, setIsEnabled] = useState(() => {
    // Em desenvolvimento, sempre ativo
    if (process.env.NODE_ENV === 'development') {
      return true;
    }
    
    // Em produção, verificar localStorage
    const stored = localStorage.getItem(storageKey);
    return stored === 'true' ? true : defaultValue;
  });

  useEffect(() => {
    // Listener para mudanças no localStorage (sincronização entre abas)
    const handleStorageChange = (e) => {
      if (e.key === storageKey) {
        setIsEnabled(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Listener customizado para mudanças na mesma aba
    const handleLocalChange = () => {
      const stored = localStorage.getItem(storageKey);
      setIsEnabled(stored === 'true');
    };

    window.addEventListener('featureFlagChanged', handleLocalChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('featureFlagChanged', handleLocalChange);
    };
  }, [storageKey]);

  const enable = () => {
    localStorage.setItem(storageKey, 'true');
    setIsEnabled(true);
    window.dispatchEvent(new Event('featureFlagChanged'));
  };

  const disable = () => {
    localStorage.removeItem(storageKey);
    setIsEnabled(false);
    window.dispatchEvent(new Event('featureFlagChanged'));
  };

  return { isEnabled, enable, disable };
};
